import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// White Elephant Triptych — three cinematic serving-ritual cards (one per
// variant). Each card carries its variant colour, the bottle, a 3-step pour,
// and the recommended pairing spirit. Sits between BrandHighlights and
// BrandProductLine on the White Elephant brand page only.
// -----------------------------------------------------------------------------
const IVORY = "#F6EFE0";
const CHARCOAL = "#0F1A12";
const GOLD = "#C9962D";

// Six additional classic cocktails using the White Elephant tonic range.
// Two per tonic — colour keyed to the tonic used. Copy pulled from
// we.moreCocktails.*; the fields below are dev safety fallbacks.
const MORE_COCKTAILS = [
  {
    key: "yuzuSour", accent: "#C9962D",
    fbPairing: "Yuzu · vodka",
    fbName: "Yuzu Sour",
    fbRecipe: "45 ml vodka · 15 ml lemon · top with Yuzu Tonic · egg-white foam · yuzu peel.",
  },
  {
    key: "goldenHighball", accent: "#C9962D",
    fbPairing: "Yuzu · Japanese whisky",
    fbName: "Golden Highball",
    fbRecipe: "45 ml Japanese whisky · large ice · top with Yuzu Tonic · lemon coin.",
  },
  {
    key: "pinkPaloma", accent: "#D6377E",
    fbPairing: "Barbados · blanco tequila",
    fbName: "Pink Paloma",
    fbRecipe: "45 ml tequila blanco · pinch of salt · top with Barbados Breeze · grapefruit wedge.",
  },
  {
    key: "caribbeanSpritz", accent: "#D6377E",
    fbPairing: "Barbados · aged rum + prosecco",
    fbName: "Caribbean Spritz",
    fbRecipe: "30 ml aged rum · 60 ml prosecco · top with Barbados Breeze · orange twist.",
  },
  {
    key: "sicilianGT", accent: "#4E8A3C",
    fbPairing: "Bitter Lemon · Mediterranean gin",
    fbName: "Sicilian G&T",
    fbRecipe: "45 ml Mediterranean gin · rosemary sprig · top with Bitter Lemon · lemon wheel.",
  },
  {
    key: "vodkaBitter", accent: "#4E8A3C",
    fbPairing: "Bitter Lemon · vodka",
    fbName: "Cucumber Bitter",
    fbRecipe: "45 ml vodka · 3 cucumber ribbons · top with Bitter Lemon · mint sprig.",
  },
];

// Variant identities — colour-coded to match each bottle's cap/label.
const VARIANTS = [
  {
    key: "yuzu",
    image: "/products/p07.webp",
    accent: "#C9962D", // amber gold — Yuzu
    tint: "#FFF7DA",
  },
  {
    key: "barbados",
    image: "/products/p09.webp",
    accent: "#D6377E", // pink magenta — Barbados
    tint: "#FDE6EE",
  },
  {
    key: "bitter",
    image: "/products/p08.webp",
    accent: "#4E8A3C", // fresh green — Bitter Lemon
    tint: "#EAF6DE",
  },
];

// Fallback default copy — used only when the i18n key is missing (dev safety).
const FALLBACK = {
  yuzu: {
    name: "Japanese Yuzu Tonic",
    pairing: "Best with gin",
    steps: [
      "Fill a highball with cubed ice",
      "Pour 45 ml gin, top with Yuzu tonic",
      "Garnish with a shiso leaf or lemon peel",
    ],
  },
  barbados: {
    name: "Barbados Breeze Tonic",
    pairing: "Best with aged white rum",
    steps: [
      "Fill a copa glass with crushed ice",
      "Pour 45 ml aged white rum, top with Barbados Breeze",
      "Finish with a pink grapefruit peel",
    ],
  },
  bitter: {
    name: "Bitter Lemon",
    pairing: "Best with gin or vodka",
    steps: [
      "Fill a highball with cubed ice",
      "Pour 45 ml gin or vodka, top with Bitter Lemon",
      "Garnish with a lemon twist and mint sprig",
    ],
  },
};

function VariantCard({ variant, index }) {
  const { t } = useT();
  const fb = FALLBACK[variant.key];
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const nameRaw = t(`brandContent.white-elephant.triptych.${variant.key}.name`);
  const nameStr = isTranslated(nameRaw) ? nameRaw : fb.name;
  const pairingRaw = t(`brandContent.white-elephant.triptych.${variant.key}.pairing`);
  const pairingStr = isTranslated(pairingRaw) ? pairingRaw : fb.pairing;
  const trSteps = t(`brandContent.white-elephant.triptych.${variant.key}.steps`);
  const steps = Array.isArray(trSteps) && trSteps.length === 3 ? trSteps : fb.steps;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.08 }}
      className="relative overflow-hidden rounded-3xl shadow-[0_20px_60px_-25px_rgba(15,26,18,0.4)] flex flex-col"
      style={{ background: variant.tint, borderTop: `4px solid ${variant.accent}` }}
      data-testid={`we-triptych-${variant.key}`}
    >
      {/* Bottle photo — pinned top, centred, transparent background */}
      <div className="relative flex items-end justify-center pt-10 pb-6 px-6" style={{ minHeight: "320px" }}>
        <img
          src={variant.image}
          alt={nameStr}
          className="h-[280px] w-auto object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.15)]"
        />
      </div>

      <div className="px-8 pb-8 flex-1 flex flex-col">
        <p
          className="text-[0.65rem] font-bold uppercase mb-3"
          style={{ color: variant.accent, letterSpacing: "0.32em" }}
        >
          {pairingStr}
        </p>

        <h3
          className="font-editorial italic leading-[1.05] text-2xl md:text-3xl mb-6"
          style={{ color: CHARCOAL }}
        >
          {nameStr}
        </h3>

        {/* Golden hairline */}
        <div
          className="h-px w-16 mb-6"
          style={{
            background: `linear-gradient(90deg, ${variant.accent}, transparent)`,
          }}
        />

        {/* 3-step serving ritual */}
        <ol className="space-y-4">
          {steps.map((label, i) => (
            <li
              key={label}
              className="border-t pt-3"
              style={{ borderColor: `${variant.accent}44` }}
            >
              <span
                className="font-editorial italic text-2xl block"
                style={{ color: variant.accent }}
              >
                {["I", "II", "III"][i]}.
              </span>
              <span
                className="mt-1 block text-sm leading-snug"
                style={{ color: CHARCOAL }}
              >
                {label}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </motion.article>
  );
}

export default function WhiteElephantTriptych() {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const overlineRaw = t("brandContent.white-elephant.triptych.overline");
  const overline = isTranslated(overlineRaw)
    ? overlineRaw
    : "Three tonics, three moments";
  const titleRaw = t("brandContent.white-elephant.triptych.title");
  const title = isTranslated(titleRaw) ? titleRaw : "The bartender's shortlist.";

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: IVORY, color: CHARCOAL }}
      data-testid="we-triptych"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        <div className="max-w-2xl mb-14">
          <p
            className="text-[0.7rem] md:text-xs font-bold uppercase mb-4"
            style={{ color: "#B02224", letterSpacing: "0.36em" }}
          >
            {overline}
          </p>
          <h2
            className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
            style={{ color: CHARCOAL }}
          >
            {title}
          </h2>
          <div
            className="mt-6 h-px w-24"
            style={{
              background: `linear-gradient(90deg, ${GOLD}, transparent)`,
            }}
          />
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {VARIANTS.map((v, i) => (
            <VariantCard key={v.key} variant={v} index={i} />
          ))}
        </div>

        {/* Six more classic cocktails using the White Elephant tonic range */}
        <div className="mt-16 lg:mt-20">
          <p
            className="text-[0.65rem] md:text-xs font-bold uppercase mb-3"
            style={{ color: "#B02224", letterSpacing: "0.32em" }}
          >
            {t("we.moreCocktails.overline") === "we.moreCocktails.overline"
              ? "More from the bar"
              : t("we.moreCocktails.overline")}
          </p>
          <h3
            className="font-editorial italic leading-[1.1] text-3xl md:text-4xl mb-10"
            style={{ color: CHARCOAL }}
          >
            {t("we.moreCocktails.title") === "we.moreCocktails.title"
              ? "Six more with White Elephant."
              : t("we.moreCocktails.title")}
          </h3>

          <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
            {MORE_COCKTAILS.map((c, i) => (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-2xl p-6 lg:p-7 border"
                style={{ background: "#FFFCF4", borderColor: `${c.accent}33` }}
                data-testid={`we-more-cocktail-${c.key}`}
              >
                <div
                  className="text-[0.6rem] font-bold uppercase tracking-[0.28em] mb-2"
                  style={{ color: c.accent }}
                >
                  {t(`we.moreCocktails.${c.key}.pairing`) === `we.moreCocktails.${c.key}.pairing`
                    ? c.fbPairing
                    : t(`we.moreCocktails.${c.key}.pairing`)}
                </div>
                <h4
                  className="font-editorial italic text-xl md:text-2xl leading-[1.1] mb-3"
                  style={{ color: CHARCOAL }}
                >
                  {t(`we.moreCocktails.${c.key}.name`) === `we.moreCocktails.${c.key}.name`
                    ? c.fbName
                    : t(`we.moreCocktails.${c.key}.name`)}
                </h4>
                <p className="text-sm leading-[1.55] text-black/65">
                  {t(`we.moreCocktails.${c.key}.recipe`) === `we.moreCocktails.${c.key}.recipe`
                    ? c.fbRecipe
                    : t(`we.moreCocktails.${c.key}.recipe`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Fine bottom divider — signature gold hairline */}
      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
